import { useUser } from "@clerk/clerk-react";
import { AlertTriangle, ArrowLeft, FileText, PoundSterling, Tag } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Spinner from "../components/Spinner";
import { apiClient } from "../lib/apiClient";

interface CreatePaymentData {
	name: string;
	email: string;
	description: string;
	amount: number;
	currency: string;
	category: string;
}

export default function NewPayment() {
	const navigate = useNavigate();
	const { user } = useUser();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [hasTermsDocument, setHasTermsDocument] = useState<boolean | null>(null);
	const [checkingTerms, setCheckingTerms] = useState(true);

	const [formData, setFormData] = useState<CreatePaymentData>({
		name: "",
		email: "",
		description: "",
		amount: 0,
		currency: "gbp",
		category: "tattoo",
	});

	useEffect(() => {
		const checkTermsDocument = async () => {
			if (!user?.id) return;

			if (user.publicMetadata?.role === "member") {
				setHasTermsDocument(true);
				setCheckingTerms(false);
				return;
			}
			try {
				setCheckingTerms(true);
				const response = await apiClient().get("/term/latest/terms_and_conditions");

				if (response.data === "") {
					setHasTermsDocument(null);
					return;
				}
				setHasTermsDocument(true);
			} catch (error) {
				console.error("Failed to check terms document:", error);
				// If the API call fails, assume no document exists to be safe
				setHasTermsDocument(false);
			} finally {
				setCheckingTerms(false);
			}
		};

		// Only run if we have a user ID and haven't checked yet
		if (user?.id && hasTermsDocument === null) {
			checkTermsDocument();
		}
	}, [user, hasTermsDocument]);

	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: name === "amount" ? Number.parseFloat(value) * 100 : value, // Convert pounds to pence
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!user) {
			setError("Please sign in to create a payment");
			return;
		}

		if (formData.amount < 100) {
			// Minimum £1.00
			setError("Amount must be at least £1.00");
			return;
		}

		setIsLoading(true);
		setError(null);

		try {
			const response = await apiClient().post<{
				id: string;
				paymentUrl: string;
			}>("/payments", formData);

			// Redirect to the payment success/sharing page
			navigate(`/payments/${response.data.id}`);
			// biome-ignore lint/suspicious/noExplicitAny: <error must be any or unknown>
		} catch (err: any) {
			console.error("Failed to create payment:", err);
			setError(err.response?.data?.message || "Failed to create payment. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
				{/* Header */}
				<div className="max-w-2xl mx-auto mb-6 sm:mb-8">
					<div className="flex items-center gap-3 sm:gap-4">
						<Link
							to="/"
							className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors p-2 sm:p-0 -ml-2 sm:ml-0 touch-manipulation"
						>
							<ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
							<span className="hidden sm:inline">Back</span>
						</Link>
						<h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
							Create New Payment
						</h1>
					</div>
				</div>

				{/* Terms Document Warning */}
				{!checkingTerms && !hasTermsDocument && (
					<div className="max-w-2xl mx-auto mb-6">
						<div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
							<div className="flex items-start gap-3">
								<AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
								<div className="flex-1">
									<p className="text-amber-800 text-sm sm:text-base font-medium mb-1">
										Terms and Conditions Required
									</p>
									<p className="text-amber-700 text-sm mb-3">
										You need to upload your terms and conditions document before you can create
										payment links.
									</p>
									<Link
										to="/settings/termsandconditions"
										className="inline-flex items-center gap-1 text-sm text-amber-800 hover:text-amber-900 underline font-medium"
									>
										Upload Terms & Conditions
										<ArrowLeft className="w-4 h-4 rotate-180" />
									</Link>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Main Form */}
				<div className="max-w-2xl mx-auto">
					<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
						<form onSubmit={handleSubmit} className="space-y-6">
							{/* Title */}
							<div>
								<label
									htmlFor="name"
									className="block text-sm sm:text-base font-medium text-gray-700 mb-2"
								>
									Name
								</label>
								<div className="relative">
									<FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
									<input
										type="text"
										id="name"
										name="name"
										required
										value={formData.name}
										onChange={handleInputChange}
										placeholder="Nicolas Cage"
										className="w-full pl-12 pr-4 py-4 sm:py-3 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent touch-manipulation"
									/>
								</div>
							</div>

							{/* Email */}
							<div>
								<label
									htmlFor="email"
									className="block text-sm sm:text-base font-medium text-gray-700 mb-2"
								>
									Email
								</label>
								<div className="relative">
									<FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
									<input
										data-1p-ignore
										data-lp-ignore={true}
										type="email"
										id="email"
										name="email"
										autoComplete="off"
										autoCapitalize="off"
										required
										value={formData.email}
										onChange={handleInputChange}
										placeholder="nicolas_cage@example.com"
										className="w-full pl-12 pr-4 py-4 sm:py-3 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent touch-manipulation"
									/>
								</div>
							</div>

							{/* Description */}
							<div>
								<label
									htmlFor="description"
									className="block text-sm sm:text-base font-medium text-gray-700 mb-2"
								>
									Description
								</label>
								<textarea
									id="description"
									name="description"
									value={formData.description}
									onChange={handleInputChange}
									placeholder="Optional details about the service..."
									rows={3}
									className="w-full px-4 py-4 sm:py-3 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent touch-manipulation resize-none"
								/>
							</div>

							{/* Amount */}
							<div>
								<label
									htmlFor="amount"
									className="block text-sm sm:text-base font-medium text-gray-700 mb-2"
								>
									Amount (£) *
								</label>
								<div className="relative">
									<PoundSterling className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
									<input
										type="number"
										id="amount"
										name="amount"
										required
										min="1"
										step="0.01"
										inputMode="decimal"
										value={formData.amount / 100}
										onChange={handleInputChange}
										placeholder="0.00"
										className="w-full pl-12 pr-4 py-4 sm:py-3 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent touch-manipulation"
									/>
								</div>
							</div>

							{/* Category */}
							<div>
								<label
									htmlFor="category"
									className="block text-sm sm:text-base font-medium text-gray-700 mb-2"
								>
									Category
								</label>
								<div className="relative">
									<Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
									<select
										id="category"
										name="category"
										value={formData.category}
										onChange={handleInputChange}
										className="w-full pl-12 pr-10 py-4 sm:py-3 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none touch-manipulation bg-white"
									>
										<option value="tattoo">Tattoo</option>
										<option value="piercing">Piercing</option>
										<option value="consultation">Consultation</option>
										<option value="touch-up">Touch-up</option>
										<option value="other">Other</option>
									</select>
									<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
										<svg
											className="w-5 h-5 text-gray-400"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<title>tag icon</title>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M19 9l-7 7-7-7"
											/>
										</svg>
									</div>
								</div>
							</div>

							{/* Error Message */}
							{error && (
								<div className="p-4 bg-red-50 border border-red-200 rounded-lg">
									<p className="text-red-600 text-sm sm:text-base">{error}</p>
								</div>
							)}

							{/* Submit Button */}
							<button
								type="submit"
								disabled={isLoading || checkingTerms || !hasTermsDocument}
								className="w-full bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 disabled:from-gray-300 disabled:to-gray-300 text-white font-semibold py-4 sm:py-4 px-6 rounded-lg transition-all duration-300 shadow-lg flex items-center justify-center gap-2 text-base sm:text-lg touch-manipulation min-h-[56px]"
							>
								{isLoading ? (
									<>
										<Spinner />
										<span>Creating Payment...</span>
									</>
								) : checkingTerms ? (
									<>
										<Spinner />
										<span>Checking Requirements...</span>
									</>
								) : (
									"Create Payment Link"
								)}
							</button>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
}
